import React, { useState, useEffect } from 'react';
import { getKnowledgeGaps, KnowledgeGapsResponse } from '../../services/api';

interface ParentKnowledgeGapsProps {
  studentId: string;
}

export const ParentKnowledgeGaps: React.FC<ParentKnowledgeGapsProps> = ({ studentId }) => {
  const [data, setData] = useState<KnowledgeGapsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    getKnowledgeGaps(studentId)
      .then(setData)
      .catch((err) => setError(err.response?.data?.detail || 'Failed to load knowledge gaps'))
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) return <div className="panel-loading">Loading knowledge gaps...</div>;
  if (error) return <div className="panel-error">{error}</div>;
  if (!data || data.gaps.length === 0) {
    return <div className="panel-empty">No knowledge gaps identified. Great progress!</div>;
  }

  const severityColor = (severity: string) => {
    switch (severity) {
      case 'high': return '#dc2626';
      case 'medium': return '#f59e0b';
      case 'low': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const trendIcon = (trend: string) => {
    switch (trend) {
      case 'improving': return '\u2191';
      case 'declining': return '\u2193';
      default: return '\u2192';
    }
  };

  return (
    <div className="parent-knowledge-gaps">
      <div className="gaps-summary">
        <span className="gap-badge high">{data.summary.high} High</span>
        <span className="gap-badge medium">{data.summary.medium} Medium</span>
        <span className="gap-badge low">{data.summary.low} Low</span>
      </div>

      <div className="gaps-list">
        {data.gaps.map((gap) => (
          <div key={gap.lo_code} className="gap-item">
            <div className="gap-header">
              <span className="gap-code">{gap.description || gap.lo_code}</span>
              <span
                className="gap-severity"
                style={{ color: severityColor(gap.severity) }}
              >
                {gap.severity.toUpperCase()}
              </span>
            </div>
            <div className="gap-details">
              <span>Accuracy: {gap.accuracy}%</span>
              <span>Attempts: {gap.attempts}</span>
              <span>
                Trend: {trendIcon(gap.trend)} {gap.trend}
              </span>
              <span>Unit {gap.unit}</span>
            </div>
            {gap.error_patterns.length > 0 && (
              <div className="gap-patterns">
                <span className="patterns-label">Common errors:</span>
                {gap.error_patterns.slice(0, 2).map((p, i) => (
                  <span key={i} className="pattern-tag">{p}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
